/* Revenue Effects v5 — idempotent · GPU-friendly · Aceternity-style flip */
(function () {
  'use strict';

  const STATE_KEY = '__rvEffectsState';

  const getState = () => {
    if (!window[STATE_KEY]) {
      window[STATE_KEY] = { observer: null, hydrated: false };
    }
    return window[STATE_KEY];
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ensureRevealObserver = () => {
    const state = getState();
    if (state.observer || !('IntersectionObserver' in window)) return state.observer;

    state.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('rv-visible');
          state.observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    return state.observer;
  };

  const setupSpotlight = (root) => {
    for (const card of root.querySelectorAll('.rv-spot')) {
      if (card.dataset.rvSpotInit === 'true') continue;
      card.dataset.rvSpotInit = 'true';

      let ticking = false;
      card.addEventListener('mousemove', (event) => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const rect = card.getBoundingClientRect();
          card.style.setProperty('--mx', `${event.clientX - rect.left}px`);
          card.style.setProperty('--my', `${event.clientY - rect.top}px`);
          ticking = false;
        });
      }, { passive: true });
    }
  };

  const setupReveal = (root) => {
    if (!('IntersectionObserver' in window)) {
      for (const el of root.querySelectorAll('.rv-reveal')) {
        el.classList.add('rv-visible');
      }
      return;
    }

    const observer = ensureRevealObserver();
    if (!observer) return;

    for (const el of root.querySelectorAll('.rv-reveal')) {
      if (el.dataset.rvRevealInit === 'true') continue;
      el.dataset.rvRevealInit = 'true';
      observer.observe(el);
    }
  };

  const setupStagger = (root) => {
    for (const container of root.querySelectorAll('[data-rv-stagger]')) {
      if (container.dataset.rvStaggerInit === 'true') continue;
      container.dataset.rvStaggerInit = 'true';
      const children = container.querySelectorAll('.rv-reveal');
      children.forEach((child, i) => {
        child.style.transitionDelay = `${Math.min(i, 8) * 0.06}s`;
      });
    }
  };

  const setupInfiniteReviews = (root) => {
    const reduceMotion = prefersReducedMotion();

    for (const mask of root.querySelectorAll('[data-rv-infinite-reviews]')) {
      if (mask.dataset.rvInfiniteInit === 'true') continue;
      mask.dataset.rvInfiniteInit = 'true';

      const track = mask.querySelector('[data-rv-infinite-track]');
      if (!track) continue;

      for (const clone of track.querySelectorAll('[data-rv-clone="true"]')) {
        clone.remove();
      }

      const baseItems = Array.from(track.children);
      if (baseItems.length < 2 || reduceMotion) {
        track.style.transform = 'translate3d(0, 0, 0)';
        continue;
      }

      const style = getComputedStyle(track);
      const gap = parseFloat(style.rowGap || style.gap || '0') || 0;
      const cycleHeight = baseItems.reduce((total, item) => total + item.getBoundingClientRect().height, 0) + (gap * (baseItems.length - 1));
      if (!cycleHeight) continue;

      const frag = document.createDocumentFragment();
      for (const item of baseItems) {
        const clone = item.cloneNode(true);
        clone.dataset.rvClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        frag.appendChild(clone);
      }
      track.appendChild(frag);

      const speed = Math.max(8, Math.min(90, parseFloat(mask.dataset.rvSpeed || '28') || 28));

      let offset = 0;
      let rafId = 0;
      let lastTs = 0;
      let running = false;
      let mounted = true;

      const tick = (ts) => {
        if (!mounted) return;

        if (!lastTs) lastTs = ts;
        const delta = Math.min((ts - lastTs) / 1000, 0.1);
        lastTs = ts;

        offset += speed * delta;
        if (offset >= cycleHeight) offset -= cycleHeight;

        track.style.transform = `translate3d(0,${-offset}px,0)`;

        rafId = window.requestAnimationFrame(tick);
      };

      const start = () => {
        if (running || !mounted) return;
        running = true;
        lastTs = 0;
        rafId = window.requestAnimationFrame(tick);
      };

      const stop = () => {
        running = false;
        window.cancelAnimationFrame(rafId);
      };

      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              start();
            } else {
              stop();
            }
          }
        },
        { threshold: 0, rootMargin: '200px 0px 200px 0px' }
      );
      visibilityObserver.observe(mask);

      mask.addEventListener('shopify:section:unload', () => {
        mounted = false;
        stop();
        visibilityObserver.disconnect();
      }, { once: true });
    }
  };

  /* ---- Aceternity-style Layout Text Flip ----
     Old word slides UP and out, new word slides UP into view.
     Container width animates smoothly to fit the new word. */
  const setupFlip = (root) => {
    const reduceMotion = prefersReducedMotion();
    const SLIDE_MS = 320;
    const PAUSE_MS = 2400;

    for (const flip of root.querySelectorAll('[data-rv-flip]')) {
      if (flip.dataset.rvFlipInit === 'true') continue;
      flip.dataset.rvFlipInit = 'true';

      const wordsAttr = flip.getAttribute('data-rv-words');
      if (!wordsAttr) continue;

      const words = wordsAttr.split('|').map((w) => w.trim()).filter(Boolean);
      if (words.length === 0) continue;

      const wordEl = flip.querySelector('[data-rv-flip-word]');
      if (!wordEl) continue;

      const slot = document.createElement('span');
      slot.className = 'rv-flip__slot';
      wordEl.parentNode.insertBefore(slot, wordEl);
      slot.appendChild(wordEl);

      let index = 0;
      wordEl.textContent = words[index];

      const lineH = parseFloat(getComputedStyle(wordEl.closest('h2, h3, p, span') || wordEl).lineHeight);
      const slotHeight = isNaN(lineH) ? wordEl.offsetHeight : lineH;
      slot.style.height = slotHeight + 'px';

      const measureWidth = (text) => {
        const probe = document.createElement('span');
        probe.className = 'rv-flip__word';
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;';
        probe.textContent = text;
        slot.appendChild(probe);
        const w = probe.offsetWidth;
        probe.remove();
        return w;
      };

      slot.style.width = measureWidth(words[index]) + 'px';

      if (words.length <= 1 || reduceMotion) continue;

      const tick = () => {
        if (!document.body.contains(flip)) return;
        if (document.hidden) {
          setTimeout(tick, 800);
          return;
        }

        index = (index + 1) % words.length;
        const nextText = words[index];
        const nextWidth = measureWidth(nextText);

        slot.style.width = nextWidth + 'px';

        wordEl.style.animation = `rv-flip-out ${SLIDE_MS}ms var(--rv-ease) forwards`;

        setTimeout(() => {
          wordEl.textContent = nextText;
          wordEl.style.animation = `rv-flip-in ${SLIDE_MS}ms var(--rv-ease) forwards`;

          setTimeout(() => {
            wordEl.style.animation = '';
            setTimeout(tick, PAUSE_MS);
          }, SLIDE_MS);
        }, SLIDE_MS);
      };

      setTimeout(tick, PAUSE_MS);
    }
  };

  const hydrate = (root = document) => {
    setupSpotlight(root);
    setupReveal(root);
    setupStagger(root);
    setupInfiniteReviews(root);
    setupFlip(root);
  };

  const init = () => {
    const state = getState();
    if (state.hydrated) {
      hydrate(document);
      return;
    }
    state.hydrated = true;
    hydrate(document);
    document.addEventListener('shopify:section:load', () => hydrate(document));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
